import { CalendarIcon, CheckIcon, PinIcon } from "@app/lib/events-admin-icons";
import { formatDateRange } from "@app/lib/events-admin-utils";
import { EventDateRangePicker } from "@app/components/event-date-range-picker";
import { InvitationLayout } from "@app/components/invitation-layout";
import { CurrencySelector } from "@app/components/currency-selector";
import {
  EditIconButton,
  InlineSavePill,
  SaveSpinner,
} from "./inline-edit-controls";
import { PlacesAutocompleteInput } from "@app/components/places-autocomplete-input";
import { useEventInvitationData } from "@app/hooks/use-event-invitation-data";
import { useEventBuilder, type SaveStatus } from "@app/hooks/use-event-builder";
import { isDeniedEventStatus, isEventInReview } from "@app/lib/event-status";
import { EventInReviewPage } from "@app/pages/event-in-review/event-in-review-page";
import { Link, Navigate, useParams } from "react-router";
import "./event-builder-page.css";

const HERO_IMAGE_SRC = `${import.meta.env.BASE_URL}images/invitation-hero.png`;
const HERO_LABEL = "YOU'RE INVITED!";
const EVENT_DATES_LABEL = "EVENT DATES";

const SAVE_STATUS_LABEL: Record<Exclude<SaveStatus, "idle">, string> = {
  saved: "Saved",
  failed: "Save failed",
};

export function EventBuilderPage() {
  const { eventId } = useParams();

  const { event, landingPage, isLoadingEvent } =
    useEventInvitationData(eventId);

  const {
    editingField,
    draftValues,
    setDraftValues,
    saveStatus,
    savingField,
    isSaving,
    isSavingDates,
    handleSaveInline,
    handleSaveDates,
    handleEditFieldChange,
  } = useEventBuilder({
    event,
    landingPage,
    onError: (error) => {
      console.warn("[EventBuilderPage] Failed to save", error);
    },
  });

  const landingPagePath = eventId
    ? `/events/${eventId}/landing-page`
    : "/manage-events";

  if (isLoadingEvent) {
    return (
      <section className="events-page">
        <p className="events-page__subtitle">Loading event...</p>
      </section>
    );
  }

  if (!event) {
    return (
      <section className="events-page">
        <div className="events-empty-state">
          <h1 className="events-page__title">Event not found</h1>
          <p className="events-page__subtitle">
            This invitation workspace could not be loaded.
          </p>
          <Link to="/manage-events" className="events-inline-link">
            Back to events
          </Link>
        </div>
      </section>
    );
  }

  if (isEventInReview(event.status)) {
    return <EventInReviewPage />;
  }

  if (isDeniedEventStatus(event.status)) {
    return <Navigate to={`/events/${event.id}`} replace />;
  }

  const heroTitle = draftValues.title || event.name;
  const heroSubtitle = draftValues.subtitle || landingPage?.subtitle || "";
  const eventDateRange = formatDateRange(event.startDate, event.endDate);
  const venueName = event.venue || event.destination;
  const venueAddress = event.venueAddress;

  return (
    <InvitationLayout
      editable
      heroImageSrc={HERO_IMAGE_SRC}
      heroLabel={HERO_LABEL}
      heroTitle={
        <div className="events-builder__field-row events-builder__field-row--center">
          {editingField === "title" ? (
            <div className="event-inline-edit event-inline-edit--headline">
              <input
                type="text"
                className="events-builder__title-input"
                value={draftValues.title}
                placeholder="Event title"
                onChange={(changeEvent) =>
                  setDraftValues((currentValues) => ({
                    ...currentValues,
                    title: changeEvent.target.value,
                  }))
                }
              />
              <button
                type="button"
                className="events-check-button"
                onClick={() => {
                  void handleSaveInline("title");
                }}
                disabled={isSaving}
                aria-label="Save title"
              >
                {savingField === "title" ? (
                  <SaveSpinner size={18} />
                ) : (
                  <CheckIcon className="size-4" />
                )}
              </button>
            </div>
          ) : (
            <>
              <h1 className="events-invitation-hero__title">{heroTitle}</h1>
              <EditIconButton
                disabled={isSaving}
                onClick={() => {
                  void handleEditFieldChange("title");
                }}
                label="Edit title"
              />
            </>
          )}
        </div>
      }
      heroTagline={
        <div className="events-builder__field-row events-builder__field-row--center">
          {editingField === "subtitle" ? (
            <div className="events-builder__tagline-edit">
              <div className="events-builder__bordered-area events-builder__bordered-area--tagline">
                <textarea
                  className="events-builder__textarea events-builder__textarea--tagline"
                  rows={2}
                  value={draftValues.subtitle}
                  placeholder="Add a tagline for your invitation"
                  onChange={(changeEvent) =>
                    setDraftValues((currentValues) => ({
                      ...currentValues,
                      subtitle: changeEvent.target.value,
                    }))
                  }
                />
              </div>
              <InlineSavePill
                saving={savingField === "subtitle"}
                disabled={isSaving}
                onSave={() => {
                  void handleSaveInline("subtitle");
                }}
              />
            </div>
          ) : (
            <>
              <p className="events-invitation-hero__tagline">
                {heroSubtitle || "Add a tagline for your invitation"}
              </p>
              <EditIconButton
                disabled={isSaving}
                onClick={() => {
                  void handleEditFieldChange("subtitle");
                }}
                label="Edit tagline"
              />
            </>
          )}
        </div>
      }
      heroAfterTagline={
        <>
          <div className="events-builder__field-row events-builder__field-row--center events-builder__field-row--location">
            <span className="events-invitation-hero__city">
              <PinIcon className="events-invitation-hero__pin" />
              {event.destination || "Add a city"}
            </span>
          </div>

          <hr className="events-invitation-hero__divider" />

          <CurrencySelector bookingLinks={landingPage?.bookingLinks ?? []} />
        </>
      }
      detailsTitle={
        <h2 className="events-invitation-details__title">
          Create Your Personal Invitation
        </h2>
      }
      description={{
        heading: (
          <div className="events-builder__field-row">
            <h2 className="events-invitation-details__heading">Description</h2>
            {editingField === "description" ? (
              <InlineSavePill
                saving={savingField === "description"}
                disabled={isSaving}
                onSave={() => {
                  void handleSaveInline("description");
                }}
              />
            ) : (
              <EditIconButton
                disabled={isSaving}
                onClick={() => {
                  void handleEditFieldChange("description");
                }}
                label="Edit description"
              />
            )}
          </div>
        ),
        body:
          editingField === "description" ? (
            <div className="events-builder__bordered-area">
              <textarea
                className="events-builder__textarea"
                rows={4}
                value={draftValues.description}
                placeholder="Enter event description..."
                onChange={(changeEvent) =>
                  setDraftValues((currentValues) => ({
                    ...currentValues,
                    description: changeEvent.target.value,
                  }))
                }
              />
            </div>
          ) : event.description ? (
            <p className="events-invitation-details__text">
              {event.description}
            </p>
          ) : (
            <p className="events-invitation-details__text">
              No description yet. Click the pencil icon to add one.
            </p>
          ),
      }}
      venue={{
        heading: (
          <div className="events-builder__field-row">
            <h2 className="events-invitation-details__heading">Venue</h2>
            {editingField === "venue" ? (
              <InlineSavePill
                saving={savingField === "venue"}
                disabled={isSaving}
                onSave={() => {
                  void handleSaveInline("venue");
                }}
              />
            ) : (
              <EditIconButton
                disabled={isSaving}
                onClick={() => {
                  void handleEditFieldChange("venue");
                }}
                label="Edit venue"
              />
            )}
          </div>
        ),
        body: (
          <div className="events-builder__venue-fields">
            {editingField === "venue" ? (
              <div className="events-builder__underline-fields">
                <input
                  type="text"
                  className="events-builder__underline-input events-builder__underline-input--bold"
                  value={draftValues.venue}
                  placeholder="Venue name"
                  onChange={(changeEvent) =>
                    setDraftValues((currentValues) => ({
                      ...currentValues,
                      venue: changeEvent.target.value,
                    }))
                  }
                />
                <PlacesAutocompleteInput
                  value={draftValues.venueAddress}
                  placeholder="Venue address"
                  wrapperClassName="events-builder__venue-address-field"
                  onChange={(nextValue) => {
                    const normalizedValue =
                      typeof nextValue === "string"
                        ? nextValue
                        : ((
                            nextValue as {
                              target?: { value?: string };
                            }
                          )?.target?.value ?? "");

                    setDraftValues((currentValues) => ({
                      ...currentValues,
                      venueAddress: normalizedValue,
                    }));
                  }}
                  onPlaceSelect={({ address }) => {
                    setDraftValues((currentValues) => ({
                      ...currentValues,
                      venueAddress: address,
                    }));
                  }}
                />
              </div>
            ) : (
              <div className="events-invitation-details__item">
                <span className="events-invitation-details__icon events-invitation-details__icon--venue">
                  <PinIcon />
                </span>
                <div>
                  <p className="events-invitation-details__item-title">
                    {venueName}
                  </p>
                  {venueAddress ? (
                    <p className="events-invitation-details__item-subtitle">
                      {venueAddress}
                    </p>
                  ) : (
                    <p className="events-invitation-details__item-subtitle">
                      Add a venue address.
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        ),
      }}
      dates={{
        heading: (
          <div className="events-builder__field-row">
            <h2 className="events-invitation-details__heading">Event Dates</h2>
            {editingField === "startDate" || editingField === "endDate" ? (
              <InlineSavePill
                saving={isSavingDates}
                disabled={isSaving}
                onSave={() => {
                  void handleSaveDates();
                }}
              />
            ) : (
              <EditIconButton
                disabled={isSaving}
                onClick={() => {
                  void handleEditFieldChange("startDate");
                }}
                label="Edit dates"
              />
            )}
          </div>
        ),
        body:
          editingField === "startDate" || editingField === "endDate" ? (
            <div className="events-builder__date-picker">
              <EventDateRangePicker
                startDate={draftValues.startDate.slice(0, 10)}
                endDate={draftValues.endDate.slice(0, 10)}
                onChange={(nextStartDate, nextEndDate) =>
                  setDraftValues((currentValues) => ({
                    ...currentValues,
                    startDate: nextStartDate,
                    endDate: nextEndDate,
                  }))
                }
              />
            </div>
          ) : (
            <div className="events-invitation-details__item">
              <span className="events-invitation-details__icon events-invitation-details__icon--dates">
                <CalendarIcon />
              </span>
              <div>
                <p className="events-invitation-details__item-title">
                  {eventDateRange}
                </p>
                <p className="events-invitation-details__item-label">
                  {EVENT_DATES_LABEL}
                </p>
              </div>
            </div>
          ),
      }}
      actions={
        <div className="events-builder__actions">
          {saveStatus !== "idle" ? (
            <p className="events-builder__save-status" role="status">
              {SAVE_STATUS_LABEL[saveStatus]}
            </p>
          ) : null}
          <button
            type="button"
            className="events-preview-button"
            onClick={() => {
              window.open(landingPagePath, "_blank", "noopener,noreferrer");
            }}
            disabled={isSaving}
          >
            VIEW
          </button>
        </div>
      }
    />
  );
}
